import classNames from "classnames";
import { noop } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import HBIcon from "../HBIcon";
import LoadErrorImage from "./LoadErrorImage";
import styles from "./index.module.less";
const customLoader = ({ src, width }) => {
  if (width) {
    return `${src}`;
  }
  return src;
};

/**
 *
 * @param {{
 *  src: string;
 *  preSrc?: string;
 *  srcset?: string;
 *  srcSet?: string;
 *  width?: number;
 *  height?: number;
 *  alt?: string;
 *  title?: string;
 *  style?: React.CSSProperties;
 *  className?: string;
 *  preClassName?: string;
 *  loading?: "eager" | "lazy";
 *  loader?: (props: { src: string; width: number }) => string;
 *  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
 *  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
 *  showError?: boolean;
 * }} param0
 * @returns
 */
export default function Image({
  src,
  preSrc = "",
  srcset = "",
  srcSet = "",
  width,
  height,
  alt,
  title = "",
  style = {},
  className,
  preClassName = "",
  loading = "lazy", // eager | lazy
  loader = customLoader,
  onError = noop,
  onLoad = noop,
  showError = false,
  limit = false,
  ...props
}) {
  const imgRef = useRef(null);
  const loadingRef = useRef(false);
  const [error, setError] = useState(false);
  const [preLoading, setPreLoading] = useState(!!preSrc);

  const onImgError = useCallback(
    (...props) => {
      if (showError) {
        setError(true);
      }
      onError && onError(...props);
    },
    [onError, showError]
  );

  const onImgLoad = useCallback(
    (...props) => {
      if (!loadingRef.current) {
        loadingRef.current = true;
        setPreLoading(false);
        onLoad && onLoad(...props);
      }
    },
    [onLoad]
  );

  src = loader({ src, width });

  // 缓存的图片不触发onLoad，所以判断一下，手动触发
  useEffect(() => {
    if (imgRef.current?.complete && !loadingRef.current) {
      loadingRef.current = true;
      setPreLoading(false);
      onLoad &&
        onLoad({
          target: imgRef.current,
        });
    }
  }, [onLoad]);

  return (
    <>
      <If condition={error}>
        <Then>
          <LoadErrorImage
            src={src}
            srcset={srcset || srcSet}
            width={width}
            height={height}
            limit={limit}
          />
        </Then>
        <Else>
          <>
            {preLoading ? (
              <div className={classNames([styles.pre, preClassName])}>
                <img
                  className={classNames(className, "hb-image")}
                  style={style}
                  alt={alt || title}
                  title={title}
                  src={preSrc}
                  width={width}
                  height={height}
                  loading={preSrc ? "eager" : loading}
                />
                <div className={styles.loading}>
                  <HBIcon className={styles.icon} type="replay" />
                  <div className={styles.text}>加载中</div>
                </div>
              </div>
            ) : null}
            <img
              className={classNames(className, "hb-image")}
              ref={imgRef}
              style={preLoading ? { display: "none" } : style}
              alt={alt || title}
              title={title}
              src={src}
              srcSet={srcset || srcSet}
              width={width}
              height={height}
              loading={preSrc ? "eager" : loading}
              onError={onImgError}
              onLoad={onImgLoad}
              {...props}
            />
          </>
        </Else>
      </If>
    </>
  );
}
